import { Injectable } from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { AppForm } from '../../../../../../common/forms/app.form';
import {
  ApplicationAppEntityApplicationRead,
  ApplicationAppEntityApplicationWrite
} from '../../../../../../openapi';
import { environment } from '../../../../../../../environments/environment';
import { CustomValidator } from '../../../../../../common/forms/custom.validators';

export type DeviceType = 'Urządzenie obce' | 'Reklama' | string;

@Injectable()
export class ApplicationDataForm extends AppForm {

  public deviceTypes: DeviceType[] = [
    'Urządzenie obce',
    'Reklama'
  ];

  public exitClasses: string[] = [
    'A',
    'B',
    'C1',
    'C2',
    'D',
    'E'
  ];

  constructor() {
    super({
      office: new FormControl({ value: environment.applicationSubmittedTo, disabled: true }, { nonNullable: true }),
      documentReceiptMethod: new FormControl('', { validators: [Validators.required], nonNullable: true })
    });
  }

  public createExitClassControl(): void {
    this.addControl('exitClass', new FormControl('', {
      nonNullable: true,
      validators: [Validators.required, Validators.maxLength(255)]
    }));
  }

  public get exitClass(): FormControl | null {
    return this.get('exitClass') as FormControl | null;
  }

  public get objectName(): FormControl | null {
    return this.get('objectName') as FormControl | null;
  }

  public createObjectNameControl(): void {
    this.addControl('objectName', new FormControl(null, [Validators.required, Validators.maxLength(255)]));
  }

  public get roadTrafficImpact(): FormControl | null {
    return this.get('roadTrafficImpact') as FormControl | null;
  }

  public createRoadTrafficImpactControl(): void {
    this.addControl('roadTrafficImpact', new FormControl('Nie', Validators.required));
  }

  public get agreementNumber(): FormControl | null {
    return this.get('agreementNumber') as FormControl | null;
  }

  public get agreementDate(): FormControl | null {
    return this.get('agreementDate') as FormControl | null;
  }

  public createAgreementControls(): void {
    this.addControl('agreementNumber', new FormControl(null, [Validators.required, Validators.maxLength(255)]));
    this.addControl('agreementDate', new FormControl(null, [Validators.required, CustomValidator.maxDate(new Date())]));
  }

  public get documentReceiptMethod(): FormControl {
    return this.get('documentReceiptMethod') as FormControl;
  }

  public get ePuapAddress(): FormControl | null {
    return this.get('ePuapAddress') as FormControl | null;
  }

  public get eDeliveryAddress(): FormControl | null {
    return this.get('eDeliveryAddress') as FormControl | null;
  }

  public documentReceiptMethodChange(v: string): void {
    if (v === '4d536cdd-dc3a-42f9-bf89-342e5cc0c885') {
      this.addControl(
        'ePuapAddress',
        new FormControl('', [Validators.required, Validators.maxLength(255)])
      );
    } else {
      this.removeControl('ePuapAddress');
    }
    if (v === '6bef93e4-f125-49f2-ab12-11ddc5e1e8a2') {
      this.addControl('eDeliveryAddress', new FormControl('', [Validators.required, Validators.maxLength(255)]));
    } else {
      this.removeControl('eDeliveryAddress');
    }
  }

  public createApplicationSubjectControl(): void {
    this.addControl('applicationSubject', new FormControl('', {
      validators: [Validators.required, Validators.maxLength(255)],
      nonNullable: true
    }));
  }

  public createApplicationConcernsStreetControl(): void {
    this.addControl('applicationConcernsStreet', new FormControl(null, { validators: [Validators.required, Validators.maxLength(255)] }));
  }

  public get applicationConcernsStreet(): FormControl | null {
    return this.get('applicationConcernsStreet') as FormControl | null;
  }

  public get applicationSubject(): FormControl | null {
    return this.get('applicationSubject') as FormControl | null;
  }

  public showResponsiblePersonControls(): boolean {
    return !!this.responsiblePersonPhone && !!this.responsiblePersonLastName && !!this.responsiblePersonFirstName && !!this.responsiblePersonEmail;
  }

  private get responsiblePersonFirstName(): FormControl | null {
    return this.get('responsiblePersonFirstName') as FormControl | null;
  }

  private get responsiblePersonLastName(): FormControl | null {
    return this.get('responsiblePersonLastName') as FormControl | null;
  }

  private get responsiblePersonPhone(): FormControl | null {
    return this.get('responsiblePersonPhone') as FormControl | null;
  }

  private get responsiblePersonEmail(): FormControl | null {
    return this.get('responsiblePersonEmail') as FormControl | null;
  }

  public createResponsiblePersonControls(): void {
    this.addControl('responsiblePersonFirstName', new FormControl('', [Validators.required, Validators.maxLength(255)]));
    this.addControl('responsiblePersonLastName', new FormControl('', [Validators.required, Validators.maxLength(255)]));
    this.addControl('responsiblePersonPhone', new FormControl(''));
    this.addControl('responsiblePersonEmail', new FormControl('', [Validators.email, Validators.maxLength(255)]));
  }

  private get managerFirstName(): FormControl | null {
    return this.get('managerFirstName') as FormControl | null;
  }

  private get managerLastName(): FormControl | null {
    return this.get('managerLastName') as FormControl | null;
  }

  private get managerPhone(): FormControl | null {
    return this.get('managerPhone') as FormControl | null;
  }

  private get managerEmail(): FormControl | null {
    return this.get('managerEmail') as FormControl | null;
  }

  public showManagerControls(): boolean {
    return !!this.managerFirstName && !!this.managerLastName && !!this.managerPhone && !!this.managerEmail;
  }

  public createManagerControls(): void {
    this.addControl('managerFirstName', new FormControl('', [Validators.maxLength(255)]));
    this.addControl('managerLastName', new FormControl('', [Validators.maxLength(255)]));
    this.addControl('managerPhone', new FormControl('', [Validators.maxLength(255)]));
    this.addControl('managerEmail', new FormControl('', [Validators.maxLength(255)]));
  }

  public get comments(): FormControl | null {
    return this.get('comments') as FormControl | null;
  }

  public createCommentsControl(): void {
    this.addControl('comments', new FormControl('', [Validators.required, Validators.maxLength(5000)]));
  }

  public createDeviceTypeControl(): void {
    this.addControl('deviceType', new FormControl('', {
      nonNullable: true,
      validators: [Validators.required, Validators.maxLength(255)]
    }));
  }

  public get deviceType(): FormControl | null {
    return this.get('deviceType') as FormControl | null;
  }

  public createDeviceNameControl(): void {
    this.addControl('deviceName', new FormControl('', {
      nonNullable: true,
      validators: [Validators.required, Validators.maxLength(255)]
    }));
  }

  public get deviceName(): FormControl | null {
    return this.get('deviceName') as FormControl | null;
  }

  public deviceTypeChange(v: DeviceType): void {
    if (v === 'Urządzenie obce') {
      this.addControl('deviceTypeDescription', new FormControl(null, [Validators.required, Validators.maxLength(5000)]));
    } else {
      this.removeControl('deviceTypeDescription');
    }
  }

  public get deviceTypeDescription(): FormControl | null {
    return this.get('deviceTypeDescription') as FormControl | null;
  }

  public toWriteModel(): ApplicationAppEntityApplicationWrite {
    return {
      applicationSubject: this.value?.applicationSubject || null,
      exitClass: this.value?.exitClass || null,
      documentReceiptMethod: this.value.documentReceiptMethod,
      responsiblePersonFirstName: this.value?.responsiblePersonFirstName,
      responsiblePersonLastName: this.value?.responsiblePersonLastName,
      responsiblePersonPhone: this.value?.responsiblePersonPhone,
      responsiblePersonEmail: this.value?.responsiblePersonEmail,
      managerEmail: this.value?.managerEmail,
      managerPhone: this.value?.managerPhone,
      managerFirstName: this.value?.managerFirstName,
      managerLastName: this.value?.managerLastName,
      notes: this.value?.comments,
      deviceTypeDescription: this.value?.deviceTypeDescription,
      ePuapAddress: this.ePuapAddress?.value || null,
      eDeliveryAddress: this.eDeliveryAddress?.value || null,
      applicationConcernsStreet: this.applicationConcernsStreet?.value || null,
      deviceType: this.deviceType?.value || null,
      deviceName: this.deviceName?.value || null,
      agreementNumber: this.agreementNumber?.value,
      agreementDate: this.agreementDate?.value,
      objectName: this.objectName?.value || null,
      roadTrafficImpact: this.roadTrafficImpact?.value || null
    };
  }

  public override patchFormValues(app: ApplicationAppEntityApplicationRead): void {
    this.patchValue({
      documentReceiptMethod: app?.documentReceiptMethod?.id || '',
      applicationSubject: app?.applicationSubject || '',
      exitClass: app?.exitClass || '',
      responsiblePersonFirstName: app?.responsiblePersonFirstName,
      responsiblePersonLastName: app?.responsiblePersonLastName,
      responsiblePersonPhone: app?.responsiblePersonPhone,
      responsiblePersonEmail: app?.responsiblePersonEmail,
      managerEmail: app?.managerEmail,
      managerPhone: app?.managerPhone,
      managerFirstName: app?.managerFirstName,
      managerLastName: app?.managerLastName,
      comments: app?.notes,
      deviceType: app?.deviceType || '',
      legalInterest: app?.legalInterest,
      actType: app?.actType || '',
      applicationConcernsStreet: app?.applicationConcernsStreet,
      agreementNumber: app?.agreementNumber,
      agreementDate: app?.agreementDate,
      objectName: app?.objectName,
      roadTrafficImpact: app?.roadTrafficImpact || 'Nie',
      deviceName: app?.deviceName || ''
    });

    this.deviceTypeChange(app?.deviceType || '');

    this.deviceTypeDescription?.setValue(app?.deviceTypeDescription);

    this.documentReceiptMethodChange(app?.documentReceiptMethod?.id || '');
    this.ePuapAddress?.setValue(app?.ePuapAddress || '');
    this.eDeliveryAddress?.setValue(app?.eDeliveryAddress);
  }
}
